import Avatar from '@/components/atoms/Avatar';
import EmptyList from '@/components/atoms/EmptyList';
import Input from '@/components/atoms/Input';
import Modal from '@/components/atoms/modal';
import Spinner from '@/components/atoms/Spinner';
import Text from '@/components/atoms/Text';
import { DataTable } from '@/components/template/DataTable';
import { AuthStore } from '@/state/AuthenticationStore';
import { TeamMate } from '@/types/teammate.types';
import { createColumnHelper } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import TableMenu from './TableMenu';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getWorkspaceCreatorId } from '@/utils';
import { notify } from '@/helpers';
import { ACTIONS, getBadgeColor, TEAMATEROLE } from '@/constants';
import { Label } from '@/components/ui/label';
import { useTeammate } from '@/hooks/useTeammate';
import { teamMateStore } from '@/state/TeammateStore';

type TeammatesTableProps = {
  team_mates: TeamMate[];
  loading: boolean;
  deleteTeammate: (userId: string) => void;
};

const columnHelper = createColumnHelper<TeamMate>();

const TeammatesTable: FC<TeammatesTableProps> = ({
  team_mates,
  loading,
  deleteTeammate,
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [value, setValue] = useState('');
  const [selectedTeammate, setSelectedTeammate] = useState<TeamMate | null>();

  const [editingTeammate, setEditingTeammate] = useState<TeamMate | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { upateTeammateRole } = useTeammate();

  const workspaceCreatorId = getWorkspaceCreatorId(AuthStore) ?? '';
  const user_id = AuthStore?.currentUser?._id;

  const closeDeleteModal = () => {
    setDeleteModal(false);
  };

  const openDeleteModal = () => {
    setDeleteModal(true);
  };

  const handleShowDeleteModal = (team_mate: TeamMate) => {
    if (workspaceCreatorId !== user_id) {
      notify('error', 'Not allowed to delete teammate');
      return;
    } else {
      setSelectedTeammate(team_mate);
      openDeleteModal();
    }
  };

  const handleEditTeammate = (teammate: TeamMate) => {
    setEditingTeammate({ ...teammate });
    setIsEditModalOpen(true);
  };

  const handleSaveTeammate = async () => {
    if (!editingTeammate) return;

    const payload = {
      userId: editingTeammate.user._id,
      role: editingTeammate.role,
    };

    await upateTeammateRole(payload);
    setIsEditModalOpen(false);
    setEditingTeammate(null);
  };
  const roles = [
    {
      value: TEAMATEROLE.OWNER,
      label: TEAMATEROLE.OWNER,
      description:
        'Full control of SparkHub including settings, users, billing, and account ownership.',
    },
    {
      value: TEAMATEROLE.ADMIN,
      label: TEAMATEROLE.ADMIN,
      description:
        'Manage most settings, users, billing, and integrations, except deleting the account.',
    },
    {
      value: TEAMATEROLE.MANAGER,
      label: TEAMATEROLE.MANAGER,
      description:
        'Handle campaigns, customer data, reports, and analytics but not users or billing.',
    },
    {
      value: TEAMATEROLE.AGENT,
      label: TEAMATEROLE.AGENT,
      description:
        'Respond to customers, view analytics, and trigger campaigns with limited access.',
    },
  ];

  const teammateColumns = useMemo(
    () => [
      columnHelper.accessor('user', {
        id: 'name',
        cell: info => (
          <div className='flex items-center'>
            <Avatar
              alt={info?.getValue()?.user_name}
              src={info?.getValue()?.profile_picture_url}
              size='lg'
            />
            <div className='ml-2'>
              <div className='font-medium text-[14px] text-gray-900'>
                {info?.getValue()?.user_name}
              </div>
              <div className='text-gray-500'>{info?.getValue()?.email}</div>
            </div>
          </div>
        ),
        header: () => <span>Name</span>,
      }),
      columnHelper.accessor('role', {
        id: 'role',
        cell: info => {
          const role = info.getValue();
          const displayRole = role ?? 'UNKNOWN';
          return (
            <Badge variant='outline' className={getBadgeColor(displayRole)}>
              {displayRole}
            </Badge>
          );
        },
        header: () => <span>Role</span>,
      }),
      columnHelper.accessor('status', {
        id: 'status',
        cell: info => {
          const user = info?.row?.original?.user;
          return (
            <TableMenu
              user={user}
              status={info?.getValue()}
              workspaceCreatorId={workspaceCreatorId}
              user_id={user_id}
            />
          );
        },
        header: () => <span>Status</span>,
      }),
      columnHelper.accessor('user.last_seen', {
        id: 'last_seen',
        cell: info => {
          const lastSeen = info.getValue();
          return lastSeen ? format(new Date(lastSeen), 'PPpp') : '-';
        },
        header: () => <span>Last Seen Date</span>,
      }),
      columnHelper.accessor('user._id', {
        id: 'actions',
        enableSorting: false,
        cell: info => (
          <div className='flex justify-end'>
            {info?.getValue() !== workspaceCreatorId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm'>
                    <MoreHorizontal className='w-4 h-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    onClick={() => handleEditTeammate(info?.row?.original)}
                    disabled={workspaceCreatorId !== user_id}
                    className='text-md'
                  >
                    <Edit className='w-4 h-4 mr-2' />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleShowDeleteModal(info?.row?.original)}
                    disabled={workspaceCreatorId !== user_id}
                    className='text-red-600 text-md'
                  >
                    <Trash2 className='w-4 h-4 mr-2' />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ),
        header: () => <span>Actions</span>,
      }),
    ],
    [columnHelper, team_mates, workspaceCreatorId, user_id],
  );

  return loading ? (
    <div className='h-full w-full flex flex-row justify-center items-center'>
      <Spinner size={40} color={'#033EB5'} />
    </div>
  ) : (
    <>
      <DataTable
        columns={teammateColumns as any}
        data={team_mates}
        headerClassName='bg-gray-100 text-lightBlack font-medium text-md first:w-[initial] py-2 even:w-auto last:w-auto'
        showPagination={team_mates?.length > 0}
        noDataComponent={
          <EmptyList
            listName={'teammates'}
            title='There are no'
            subTitle='Add '
            height='60vh'
          />
        }
      />

      <Modal
        show={deleteModal}
        openModal={openDeleteModal}
        closeModal={closeDeleteModal}
        title='Delete Teammate'
        className='z-[10000] inline-block py-6 my-8 w-[100%] max-w-[440px] text-left align-center  transition-all transform bg-white shadow-xl rounded-[7px]'
      >
        <div className='px-4 mt-[10px]'>
          <Text size='sm' color='#656971'>
            You are about to delete this teammate. This action cannot be undone.
            if you are sure you want to do this, type DELETE in the box below
          </Text>
          <br />
          <Input
            placeholder='Enter DELETE to confirm'
            onChange={e => {
              const inputValue = e.target.value;
              setValue(inputValue);
            }}
          />
          <div className='mt-4 text-center flex justify-between space-x-4'>
            <div className='flex flex-1'>
              <Button
                size='sm'
                variant='outline'
                onClick={() => {
                  closeDeleteModal();
                }}
                className='w-full'
              >
                Cancel
              </Button>
            </div>

            <div className='flex flex-1'>
              <Button
                size='sm'
                type='submit'
                className='w-full'
                variant='destructive'
                disabled={value !== ACTIONS?.DELETE}
                onClick={() => {
                  closeDeleteModal();
                  deleteTeammate(selectedTeammate?.user?._id as string);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className='sm:max-w-[580px]'>
          <DialogHeader>
            <DialogTitle>Edit Teammate Role</DialogTitle>
            <DialogDescription className='text-md'>
              Update the role and permissions for this team member.
            </DialogDescription>
          </DialogHeader>

          {editingTeammate && (
            <div className='space-y-4'>
              <div className='flex items-center gap-3 p-3 bg-muted/50 rounded-lg'>
                <Avatar
                  alt={editingTeammate?.user?.user_name}
                  src={editingTeammate?.user?.profile_picture_url}
                  size='lg'
                />
                <div>
                  <div className='font-medium'>
                    {editingTeammate?.user?.user_name}
                  </div>
                  <div className='text-[12px] text-muted-foreground'>
                    {editingTeammate?.user?.email}
                  </div>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='role'>Role</Label>
                <Select
                  value={editingTeammate.role}
                  onValueChange={(
                    value:
                      | TEAMATEROLE.ADMIN
                      | TEAMATEROLE.AGENT
                      | TEAMATEROLE.MANAGER,
                  ) => setEditingTeammate({ ...editingTeammate, role: value })}
                >
                  <SelectTrigger className='focus:ring-0 focus:ring-offset-0 focus-within:border focus-within:border-secondary'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className='font-medium'>{option.label}</div>
                          <div className='text-xs text-muted-foreground'>
                            {option.description}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant='outline' onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant='secondary'
              className='text-white'
              onClick={handleSaveTeammate}
              disabled={teamMateStore?.updateRole}
            >
              {teamMateStore?.updateRole ? (
                <div className='flex items-center gap-2'>
                  <Spinner size={20} color='#ffffff' />
                  Updating...
                </div>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default observer(TeammatesTable);
